import { useState } from 'react';
import * as Yup from 'yup';
import { yupToFormErrors } from 'formik';
import useCreativeStore from 'store/creativeStore';
import _ from 'lodash';

interface ImageFormValidationProps {
  defaultForm: any;
  values: any;
}

const useImageFormValidation = (
  initialState: ImageFormValidationProps = {
    defaultForm: {},
    values: {},
  },
) => {
  const { values } = initialState;
  const [errors, setErrors] = useState<any>({ basic: {}, products: '' });
  const { actionType } = useCreativeStore();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('素材名稱必填'),
    bannerImageUrl: Yup.string().notRequired(),
    bannerUrl: Yup.string()
      .required('素材網址必填')
      .test('bannerUrl-needUrl', '請輸入包含 http(s):// 的網址', (value) => {
        const urlExpression =
          /(^https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|^https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,})/;
        return !!value && urlExpression.test(value);
      })
      .test(
        'bannerUrl-needMomo',
        '網址域名應為 momoshop.com.tw 或是 momo.dm',
        (value) => {
          const urlRegex =
            /^https?:\/\/(?:[a-zA-Z0-9]+\.)?momoshop\.com\.tw(?:\/.+)?$/;

          const url3pRegex =
            /^https?:\/\/(?:[a-zA-Z0-9]+\.)?momo\.dm(?:\/.+)?$/;
          return !!value && (urlRegex.test(value) || url3pRegex.test(value));
        },
      )
      .test('bannerUrl-needCn', '請填寫符合分類頁提示的網址', (value) => {
        const cnRegex = /cn=([0-9]{10,})(?=&|\b)/;
        const url3pCategoryRegex =
          /^https:\/\/m\.momoshop\.com\.tw\/category\.momo\?cn=[^&]+&entpCode=[^&]+/;
        if (value.match(url3pCategoryRegex) && !value.match(cnRegex)) {
          return false;
        }
        return true;
      })
      .test(
        'bannerUrl-3P',
        '不支援您所輸入的 「TP」 網址，請參考下方 mo店 + 連結網址說明',
        (value) => {
          const urlTPRegex =
            /^https?:\/\/(?:[a-zA-Z0-9]+\.)?momoshop\.com\.tw\/TP(?:\/.+)?$/;
          const urlMobileTPRegex =
            /^https?:\/\/m\.momoshop\.com\.tw\/TP(?:\/.+)?$/;
          const url3pMainRegex =
            /^https:\/\/www\.momoshop\.com\.tw\/TP\/[^/]+\/main/;
          const url3pGoodsRegex =
            /^https:\/\/www\.momoshop\.com\.tw\/TP\/[^/]+\/goods/;
          const url3pGoodsDetailRegex =
            /^https:\/\/www\.momoshop\.com\.tw\/TP\/[^/]+\/goodsDetail\/[^/]+/;
          if (value.match(urlTPRegex) || value.match(urlMobileTPRegex)) {
            if (
              !value.match(url3pMainRegex) &&
              !value.match(url3pGoodsRegex) &&
              !value.match(url3pGoodsDetailRegex)
            ) {
              return false;
            }
          }
          return true;
        },
      ),

    fileType: Yup.string().test(
      'is-png',
      `檔案格式不符合，請重新上傳`,
      (value) => {
        const { updateImg } = values;
        if (actionType === 'edit' && updateImg === false) {
          return true;
        }
        return value !== undefined && value !== null && value === 'image/png';
      },
    ),
    fileSize: Yup.number()
      .max(20)
      .transform((value) => (Number.isNaN(value) ? null : value))
      .test('is-positive', `檔案大小須小於 2MB`, (value) => {
        const { updateImg } = values;
        if (actionType === 'edit' && updateImg === false) {
          return true;
        }
        return value !== undefined && value !== null && value <= 20;
      }),
    typeProperties: Yup.object().shape({
      width: Yup.number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .test('is-positive', `檔案寬度不符合`, (value) => {
          const { height } = values.typeProperties;
          if (height === 60) {
            return value !== undefined && value !== null && value === 1000;
          }
          if (height === 320) {
            return value !== undefined && value !== null && value === 960;
          }
          return false;
        }),
      height: Yup.number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .test('is-positive', `檔案高度不符合`, (value) => {
          const { width } = values.typeProperties;
          if (width === 1000) {
            return value !== undefined && value !== null && value === 60;
          }
          if (width === 960) {
            return value !== undefined && value !== null && value === 320;
          }
          return false;
        }),
      imageUrl: Yup.string().notRequired(),
      product: Yup.object().test(
        'is-required',
        '尚未選擇主打品',
        (value) => !_.isEmpty(value),
      ),
    }),
  });

  // 即時驗證表單
  const handleValidation = () => {
    validationSchema
      .validate(values, { abortEarly: false })
      .then(function () {
        setErrors({});
      })
      .catch(function (err) {
        // console.log('err', err);
        setErrors(yupToFormErrors(err));
        console.log('errors', yupToFormErrors(err));
      });
  };

  return { errors, setErrors, handleValidation };
};

export default useImageFormValidation;
